/* eslint-disable react/jsx-no-target-blank */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub'
import { AsideMenuItem } from './AsideMenuItem'
import { usePageData } from '../../core'
import type { SiteOption } from '../../core'
import { PAGE_PERMISSION } from '../../../../app/modules/roles/constant/role-default'
import { getLocations } from '../../../../app/modules/locations/api'

type SiteResponseItem = {
  id?: number | string | null
  name?: string | null
  ouName?: string | null
  organizationUnitName?: string | null
  organizationUnitLongshipId?: string | null
  organizationUnitParentId?: string | null
  organizationUnitHierarchyIds?: Array<string | null> | null
  organizationUnitHierarchyNames?: Array<string | null> | null
}

const SITE_PAGE_SIZE = 100

type SiteTreeNode = {
  id: string
  name: string
  childOrganizationUnits: SiteTreeNode[]
  locations: SiteOption[]
}

type SiteTreeData = {
  topLevelSites: SiteOption[]
  rootNodes: SiteTreeNode[]
  ungroupedSites: SiteOption[]
  sitePathById: Map<number, string[]>
  siteIdsByNodeId: Map<string, number[]>
}

type SiteTreeItemProps = {
  node: SiteTreeNode
  expandedNodeIds: Record<string, boolean>
  toggleNode: (nodeId: string) => void
  selectedSiteId: number | null
  selectedSiteIds: number[] | null
  setSelectedSiteId: (siteId: number | null) => void
  setSelectedSiteIds: (siteIds: number[] | null) => void
  siteIdsByNodeId: Map<string, number[]>
  isSiteLoading: boolean
  forceExpanded?: boolean
  onSiteSelected?: () => void
  visitedNodeIds?: Set<string>
}

const toSanitizedStringArray = (value: Array<string | null> | null | undefined) =>
  (Array.isArray(value) ? value : [])
    .map((item) => item?.trim() ?? '')
    .filter((item) => item.length > 0)

const toSiteOptions = (items: SiteResponseItem[]): SiteOption[] => {
  const uniqueSites = new Map<number, SiteOption>()

  items.forEach((item) => {
    if (item.id === null || item.id === undefined) {
      return
    }

    const numericId = Number(item.id)
    if (!Number.isFinite(numericId)) {
      return
    }

    const existing = uniqueSites.get(numericId)
    const trimmedName = item.name?.trim()
    const organizationUnitName = item.organizationUnitName?.trim() || item.ouName?.trim() || ''
    const organizationUnitLongshipId = item.organizationUnitLongshipId?.trim() || ''
    const organizationUnitParentId = item.organizationUnitParentId?.trim() || ''
    const organizationUnitHierarchyIds = toSanitizedStringArray(item.organizationUnitHierarchyIds)
    const organizationUnitHierarchyNames = toSanitizedStringArray(item.organizationUnitHierarchyNames)

    uniqueSites.set(numericId, {
      id: numericId,
      name: trimmedName || existing?.name || `Site ${numericId}`,
      organizationUnitName: organizationUnitName || existing?.organizationUnitName || undefined,
      organizationUnitLongshipId: organizationUnitLongshipId || existing?.organizationUnitLongshipId || undefined,
      organizationUnitParentId: organizationUnitParentId || existing?.organizationUnitParentId || undefined,
      organizationUnitHierarchyIds:
        organizationUnitHierarchyIds.length > 0
          ? organizationUnitHierarchyIds
          : existing?.organizationUnitHierarchyIds,
      organizationUnitHierarchyNames:
        organizationUnitHierarchyNames.length > 0
          ? organizationUnitHierarchyNames
          : existing?.organizationUnitHierarchyNames,
    })
  })

  return Array.from(uniqueSites.values()).sort((left, right) => left.name.localeCompare(right.name))
}

const matchesSearchTerm = (value: string | null | undefined, searchTerm: string) =>
  (value ?? '').trim().toLowerCase().includes(searchTerm)

const SELECTED_SITE_OPTION_COLOR = '#44d62c'

const normalizeSiteIds = (siteIds: number[] | null | undefined) => {
  if (!Array.isArray(siteIds)) {
    return []
  }

  return Array.from(
    new Set(
      siteIds
        .map((siteId) => Number(siteId))
        .filter((siteId) => Number.isFinite(siteId))
    )
  ).sort((left, right) => left - right)
}

const areSameSiteIdSets = (left: number[] | null | undefined, right: number[] | null | undefined) => {
  const normalizedLeft = normalizeSiteIds(left)
  const normalizedRight = normalizeSiteIds(right)

  if (normalizedLeft.length !== normalizedRight.length) {
    return false
  }

  return normalizedLeft.every((siteId, index) => siteId === normalizedRight[index])
}

const isSiteSelected = (
  selectedSiteId: number | null,
  selectedSiteIds: number[] | null | undefined,
  siteId: number
) => {
  if (selectedSiteId === siteId) {
    return true
  }

  if (selectedSiteId !== null) {
    return false
  }

  return areSameSiteIdSets(selectedSiteIds, [siteId])
}

const getSiteOptionClassName = (isSelected: boolean) =>
  `w-100 border-0 bg-transparent text-start py-1 px-2 d-flex align-items-center ${
    isSelected ? 'fw-semibold' : 'text-gray-700'
  }`

const getSiteIconClassName = (isSelected: boolean) =>
  `bi bi-building me-2 ${isSelected ? '' : 'text-gray-500'}`

const getSiteOptionStyle = (isSelected: boolean): React.CSSProperties | undefined =>
  isSelected ? { color: SELECTED_SITE_OPTION_COLOR } : undefined

const findNodePathById = (
  nodes: SiteTreeNode[],
  targetNodeId: string,
  visitedNodeIds: Set<string> = new Set(),
  path: string[] = []
): string[] | null => {
  for (const node of nodes) {
    if (visitedNodeIds.has(node.id)) {
      continue
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)
    const nextPath = [...path, node.id]

    if (node.id === targetNodeId) {
      return nextPath
    }

    const childPath = findNodePathById(node.childOrganizationUnits, targetNodeId, nextVisitedNodeIds, nextPath)
    if (childPath) {
      return childPath
    }
  }

  return null
}

const buildSiteTree = (sites: SiteOption[]): SiteTreeData => {
  const organizationUnitsById = new Map<string, SiteTreeNode>()
  const parentByChildId = new Map<string, string>()
  const topLevelSites: SiteOption[] = []
  const ungroupedSites: SiteOption[] = []
  const sitePathById = new Map<number, string[]>()

  const ensureNode = (id: string, name: string) => {
    const existing = organizationUnitsById.get(id)
    if (existing) {
      if ((!existing.name || existing.name === existing.id) && name && name !== existing.id) {
        existing.name = name
      }

      return existing
    }

    const node: SiteTreeNode = {
      id,
      name: name || id,
      childOrganizationUnits: [],
      locations: [],
    }

    organizationUnitsById.set(id, node)
    return node
  }

  sites.forEach((site) => {
    const hierarchyIds = toSanitizedStringArray(site.organizationUnitHierarchyIds ?? null)
    const hierarchyNames = toSanitizedStringArray(site.organizationUnitHierarchyNames ?? null)
    const fallbackOuId = site.organizationUnitLongshipId?.trim() || ''
    const fallbackOuName = site.organizationUnitName?.trim() || ''
    const fallbackParentId = site.organizationUnitParentId?.trim() || ''

    let normalizedIds = hierarchyIds.length > 0 ? hierarchyIds : fallbackOuId ? [fallbackOuId] : []
    const normalizedNames =
      hierarchyNames.length > 0 ? hierarchyNames : fallbackOuName ? [fallbackOuName] : []

    if (normalizedIds.length === 0 && normalizedNames.length > 0) {
      normalizedIds = normalizedNames.map(
        (name, index) =>
          `ou-name:${name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')}:${index}`
      )
    }

    if (normalizedIds.length === 0 && normalizedNames.length === 0) {
      ungroupedSites.push(site)
      return
    }

    const pathIds: string[] = []
    normalizedIds.forEach((rawId, index) => {
      const nodeId = rawId.trim()
      if (!nodeId) {
        return
      }

      const nodeName = normalizedNames[index]?.trim() || (index === normalizedIds.length - 1 ? fallbackOuName : '') || nodeId
      ensureNode(nodeId, nodeName)
      pathIds.push(nodeId)

      if (index > 0) {
        const parentId = normalizedIds[index - 1]?.trim()
        if (parentId && parentId !== nodeId) {
          parentByChildId.set(nodeId, parentId)
        }
      }
    })

    if (pathIds.length === 0) {
      ungroupedSites.push(site)
      return
    }

    if (pathIds.length === 1 && fallbackParentId && fallbackParentId !== pathIds[0]) {
      ensureNode(fallbackParentId, fallbackParentId)
      parentByChildId.set(pathIds[0], fallbackParentId)
    }

    const leafNodeId = pathIds[pathIds.length - 1]
    const leafNode = organizationUnitsById.get(leafNodeId)
    if (leafNode && !leafNode.locations.some((existingLocation) => existingLocation.id === site.id)) {
      leafNode.locations.push(site)
    }

    sitePathById.set(site.id, pathIds)
  })

  const rootNodes: SiteTreeNode[] = []
  organizationUnitsById.forEach((node) => {
    node.childOrganizationUnits = []
  })

  organizationUnitsById.forEach((node) => {
    const parentId = parentByChildId.get(node.id)
    if (parentId) {
      const parentNode = organizationUnitsById.get(parentId)
      if (parentNode && parentNode.id !== node.id) {
        parentNode.childOrganizationUnits.push(node)
        return
      }
    }

    rootNodes.push(node)
  })

  const sortNodes = (nodes: SiteTreeNode[], visited: Set<string> = new Set()) => {
    nodes.sort((left, right) => left.name.localeCompare(right.name))
    nodes.forEach((node) => {
      if (visited.has(node.id)) {
        return
      }

      visited.add(node.id)
      node.locations.sort((left, right) => left.name.localeCompare(right.name))
      sortNodes(node.childOrganizationUnits, visited)
    })
  }

  const normalizedRootNodes = rootNodes.flatMap((node) => {
    const isChargeHubParentGroup =
      node.name.trim().toLowerCase() === 'charge hub'

    if (isChargeHubParentGroup) {
      node.locations.forEach((site) => {
        if (!topLevelSites.some((existingSite) => existingSite.id === site.id)) {
          topLevelSites.push(site)
        }
      })
    }

    return isChargeHubParentGroup ? node.childOrganizationUnits : [node]
  })

  topLevelSites.sort((left, right) => left.name.localeCompare(right.name))
  sortNodes(normalizedRootNodes)
  ungroupedSites.sort((left, right) => left.name.localeCompare(right.name))

  const siteIdsByNodeId = new Map<string, number[]>()
  const collectSiteIds = (node: SiteTreeNode, visitedNodeIds: Set<string> = new Set()) => {
    if (visitedNodeIds.has(node.id)) {
      return [] as number[]
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)

    const siteIds = new Set<number>()
    node.locations.forEach((site) => siteIds.add(site.id))

    node.childOrganizationUnits.forEach((childNode) => {
      collectSiteIds(childNode, nextVisitedNodeIds).forEach((siteId) => {
        siteIds.add(siteId)
      })
    })

    const normalizedSiteIds = Array.from(siteIds).sort((left, right) => left - right)
    siteIdsByNodeId.set(node.id, normalizedSiteIds)
    return normalizedSiteIds
  }

  normalizedRootNodes.forEach((node) => {
    collectSiteIds(node)
  })

  return {
    topLevelSites,
    rootNodes: normalizedRootNodes,
    ungroupedSites,
    sitePathById,
    siteIdsByNodeId,
  }
}

const filterSiteTreeNodes = (
  nodes: SiteTreeNode[],
  searchTerm: string,
  visitedNodeIds: Set<string> = new Set<string>()
): SiteTreeNode[] => {
  const filteredNodes: SiteTreeNode[] = []

  nodes.forEach((node) => {
    if (visitedNodeIds.has(node.id)) {
      return
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)

    const nodeMatches = matchesSearchTerm(node.name, searchTerm)
    if (nodeMatches) {
      filteredNodes.push(node)
      return
    }

    const filteredLocations = node.locations.filter((site) => matchesSearchTerm(site.name, searchTerm))
    const filteredChildren = filterSiteTreeNodes(node.childOrganizationUnits, searchTerm, nextVisitedNodeIds)

    if (filteredLocations.length === 0 && filteredChildren.length === 0) {
      return
    }

    filteredNodes.push({
      ...node,
      locations: filteredLocations,
      childOrganizationUnits: filteredChildren,
    })
  })

  return filteredNodes
}

const SiteTreeItem: React.FC<SiteTreeItemProps> = ({
  node,
  expandedNodeIds,
  toggleNode,
  selectedSiteId,
  selectedSiteIds,
  setSelectedSiteId,
  setSelectedSiteIds,
  siteIdsByNodeId,
  isSiteLoading,
  forceExpanded = false,
  onSiteSelected,
  visitedNodeIds = new Set<string>(),
}) => {
  if (visitedNodeIds.has(node.id)) {
    return null
  }

  const nextVisitedNodeIds = new Set(visitedNodeIds)
  nextVisitedNodeIds.add(node.id)
  const hasChildren = node.childOrganizationUnits.length > 0 || node.locations.length > 0
  const isExpanded = forceExpanded || Boolean(expandedNodeIds[node.id])
  const canToggle = hasChildren && !forceExpanded
  const nodeSiteIds = siteIdsByNodeId.get(node.id) ?? []
  const isGroupSelected = selectedSiteId === null && areSameSiteIdSets(selectedSiteIds, nodeSiteIds)

  return (
    <div className='mb-1'>
      <div className='d-flex align-items-center'>
        {canToggle ? (
          <button
            type='button'
            className='btn btn-icon btn-sm btn-light w-25px h-25px'
            onClick={() => toggleNode(node.id)}
            disabled={isSiteLoading}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}`}
          >
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} fs-7`}></i>
          </button>
        ) : (
          <span className='d-inline-block w-25px'></span>
        )}
        {nodeSiteIds.length > 0 ? (
          <button
            type='button'
            className={`${getSiteOptionClassName(isGroupSelected)} text-truncate`}
            style={getSiteOptionStyle(isGroupSelected)}
            data-site-option-selected={isGroupSelected ? 'true' : undefined}
            onClick={() => {
              setSelectedSiteIds(nodeSiteIds)
              onSiteSelected?.()
            }}
            disabled={isSiteLoading}
            title={`${node.name} (${nodeSiteIds.length} site${nodeSiteIds.length === 1 ? '' : 's'})`}
          >
            {node.name}
          </button>
        ) : (
          <span className='text-gray-700 fw-semibold fs-8 text-truncate' title={node.name}>
            {node.name}
          </span>
        )}
      </div>

      {hasChildren && isExpanded ? (
        <div className='ms-7 mt-1'>
          {node.locations.map((site) => {
            const isSelected = isSiteSelected(selectedSiteId, selectedSiteIds, site.id)
            return (
              <button
                key={site.id}
                type='button'
                className={`${getSiteOptionClassName(isSelected)} mb-1`}
                style={getSiteOptionStyle(isSelected)}
                data-site-option-selected={isSelected ? 'true' : undefined}
                onClick={() => {
                  setSelectedSiteId(site.id)
                  onSiteSelected?.()
                }}
                disabled={isSiteLoading}
                title={site.name}
              >
                <i className={getSiteIconClassName(isSelected)}></i>
                {site.name}
              </button>
            )
          })}

          {node.childOrganizationUnits.map((childNode) => (
            <SiteTreeItem
              key={childNode.id}
              node={childNode}
              expandedNodeIds={expandedNodeIds}
              toggleNode={toggleNode}
              selectedSiteId={selectedSiteId}
              selectedSiteIds={selectedSiteIds}
              setSelectedSiteId={setSelectedSiteId}
              setSelectedSiteIds={setSelectedSiteIds}
              siteIdsByNodeId={siteIdsByNodeId}
              isSiteLoading={isSiteLoading}
              forceExpanded={forceExpanded}
              onSiteSelected={onSiteSelected}
              visitedNodeIds={nextVisitedNodeIds}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function AsideMenuMain() {
  const intl = useIntl()
  const [isSiteLoading, setIsSiteLoading] = useState(false)
  const [hasResolvedSiteLoad, setHasResolvedSiteLoad] = useState(false)
  const [isSiteSelectorOpen, setIsSiteSelectorOpen] = useState(false)
  const [siteSearchTerm, setSiteSearchTerm] = useState('')
  const {
    currentUser,
    siteOptions,
    setSiteOptions,
    selectedSiteId,
    setSelectedSiteId,
    selectedSiteIds,
    setSelectedSiteIds,
  } = usePageData()
  const userRoleKey = useMemo(() => currentUser.userRoles.join('|'), [currentUser.userRoles])
  const siteSelectorRef = useRef<HTMLDivElement | null>(null)
  const hasAutoScrolledOnOpenRef = useRef(false)

  useEffect(() => {
    let isMounted = true

    const loadSites = async () => {
      if (isMounted) {
        setHasResolvedSiteLoad(false)
      }
      setIsSiteLoading(true)
      try {
        let skip = 0
        let totalCount = 0
        const allItems: SiteResponseItem[] = []

        do {
          const response = await getLocations(skip, SITE_PAGE_SIZE, { field: 'name', dir: 'asc' })
          const pageItems = Array.isArray(response?.data?.items) ? (response.data.items as SiteResponseItem[]) : []
          totalCount = Number(response?.data?.totalCount ?? pageItems.length)
          allItems.push(...pageItems)
          skip += SITE_PAGE_SIZE
        } while (skip < totalCount)

        if (!isMounted) {
          return
        }

        const availableSites = toSiteOptions(allItems)
        setSiteOptions(availableSites)
      } catch {
        if (!isMounted) {
          return
        }

        setSiteOptions([])
      } finally {
        if (isMounted) {
          setIsSiteLoading(false)
          setHasResolvedSiteLoad(true)
        }
      }
    }

    loadSites()
    return () => {
      isMounted = false
    }
  }, [currentUser.id, setSiteOptions, userRoleKey])

  useEffect(() => {
    if (!hasResolvedSiteLoad) {
      return
    }

    if (siteOptions.length === 0) {
      if (selectedSiteId !== null || (selectedSiteIds !== null && selectedSiteIds.length > 0)) {
        setSelectedSiteIds(null)
      }
      return
    }

    const availableSiteIds = new Set(siteOptions.map((site) => site.id))
    const normalizedSelectedSiteIds = normalizeSiteIds(selectedSiteIds)

    if (normalizedSelectedSiteIds.length > 0) {
      const availableSelectedSiteIds = normalizedSelectedSiteIds.filter((siteId) => availableSiteIds.has(siteId))
      if (availableSelectedSiteIds.length === 0) {
        setSelectedSiteIds(null)
        return
      }

      if (!areSameSiteIdSets(availableSelectedSiteIds, normalizedSelectedSiteIds)) {
        setSelectedSiteIds(availableSelectedSiteIds)
        return
      }
    }

    if (selectedSiteId !== null && !availableSiteIds.has(selectedSiteId)) {
      setSelectedSiteIds(null)
    }
  }, [hasResolvedSiteLoad, selectedSiteId, selectedSiteIds, setSelectedSiteIds, siteOptions])

  const [expandedNodeIds, setExpandedNodeIds] = useState<Record<string, boolean>>({})
  const { topLevelSites, rootNodes, ungroupedSites, sitePathById, siteIdsByNodeId } = useMemo(
    () => buildSiteTree(siteOptions),
    [siteOptions]
  )
  const normalizedSelectedSiteIds = useMemo(() => {
    const normalized = normalizeSiteIds(selectedSiteIds)
    return normalized.length > 0 ? normalized : null
  }, [selectedSiteIds])
  const selectedSite = useMemo(
    () => {
      if (selectedSiteId !== null) {
        return siteOptions.find((site) => site.id === selectedSiteId) ?? null
      }

      if (normalizedSelectedSiteIds?.length === 1) {
        return siteOptions.find((site) => site.id === normalizedSelectedSiteIds[0]) ?? null
      }

      return null
    },
    [normalizedSelectedSiteIds, selectedSiteId, siteOptions]
  )
  const selectedGroupNode = useMemo(() => {
    if (selectedSite !== null || normalizedSelectedSiteIds === null) {
      return null
    }

    const findMatchingNode = (nodes: SiteTreeNode[], visitedNodeIds: Set<string> = new Set()): SiteTreeNode | null => {
      for (const node of nodes) {
        if (visitedNodeIds.has(node.id)) {
          continue
        }

        const nextVisitedNodeIds = new Set(visitedNodeIds)
        nextVisitedNodeIds.add(node.id)
        const nodeSiteIds = siteIdsByNodeId.get(node.id) ?? []
        if (areSameSiteIdSets(nodeSiteIds, normalizedSelectedSiteIds)) {
          return node
        }

        const childMatch = findMatchingNode(node.childOrganizationUnits, nextVisitedNodeIds)
        if (childMatch) {
          return childMatch
        }
      }

      return null
    }

    return findMatchingNode(rootNodes)
  }, [normalizedSelectedSiteIds, rootNodes, selectedSite, siteIdsByNodeId])
  const normalizedSiteSearchTerm = siteSearchTerm.trim().toLowerCase()
  const hasSiteSearch = normalizedSiteSearchTerm.length > 0
  const filteredTopLevelSites = useMemo(
    () =>
      hasSiteSearch
        ? topLevelSites.filter((site) => matchesSearchTerm(site.name, normalizedSiteSearchTerm))
        : topLevelSites,
    [hasSiteSearch, normalizedSiteSearchTerm, topLevelSites]
  )
  const filteredRootNodes = useMemo(
    () => (hasSiteSearch ? filterSiteTreeNodes(rootNodes, normalizedSiteSearchTerm) : rootNodes),
    [hasSiteSearch, normalizedSiteSearchTerm, rootNodes]
  )
  const filteredUngroupedSites = useMemo(
    () =>
      hasSiteSearch
        ? ungroupedSites.filter((site) => matchesSearchTerm(site.name, normalizedSiteSearchTerm))
        : ungroupedSites,
    [hasSiteSearch, normalizedSiteSearchTerm, ungroupedSites]
  )

  const selectedSiteLabel =
    selectedSite?.name ??
    selectedGroupNode?.name ??
    (normalizedSelectedSiteIds !== null
      ? `Selected Group (${normalizedSelectedSiteIds.length} site${normalizedSelectedSiteIds.length === 1 ? '' : 's'})`
      : 'Charge Hub (All Sites)')

  useEffect(() => {
    if (selectedSiteId === null) {
      return
    }

    const selectedSitePath = sitePathById.get(selectedSiteId)
    if (!selectedSitePath || selectedSitePath.length === 0) {
      return
    }

    setExpandedNodeIds((previous) => {
      let hasChanges = false
      const next = { ...previous }

      selectedSitePath.forEach((nodeId) => {
        if (!next[nodeId]) {
          next[nodeId] = true
          hasChanges = true
        }
      })

      return hasChanges ? next : previous
    })
  }, [selectedSiteId, sitePathById])

  useEffect(() => {
    if (!selectedGroupNode) {
      return
    }

    const selectedGroupPath = findNodePathById(rootNodes, selectedGroupNode.id)
    if (!selectedGroupPath || selectedGroupPath.length === 0) {
      return
    }

    setExpandedNodeIds((previous) => {
      let hasChanges = false
      const next = { ...previous }

      selectedGroupPath.forEach((nodeId) => {
        if (!next[nodeId]) {
          next[nodeId] = true
          hasChanges = true
        }
      })

      return hasChanges ? next : previous
    })
  }, [rootNodes, selectedGroupNode])

  const toggleNode = (nodeId: string) => {
    setExpandedNodeIds((previous) => ({
      ...previous,
      [nodeId]: !previous[nodeId],
    }))
  }

  useEffect(() => {
    if (!isSiteSelectorOpen) {
      setSiteSearchTerm('')
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const targetNode = event.target as Node
      if (siteSelectorRef.current?.contains(targetNode)) {
        return
      }

      setIsSiteSelectorOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isSiteSelectorOpen])

  useEffect(() => {
    if (!isSiteSelectorOpen) {
      hasAutoScrolledOnOpenRef.current = false
      return
    }

    if (isSiteLoading || hasAutoScrolledOnOpenRef.current) {
      return
    }

    hasAutoScrolledOnOpenRef.current = true
    let firstFrameId = 0
    let secondFrameId = 0

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        const selectedOption = siteSelectorRef.current?.querySelector<HTMLElement>(
          '[data-site-option-selected="true"]'
        )
        selectedOption?.scrollIntoView({block: 'nearest'})
      })
    })

    return () => {
      if (firstFrameId) {
        window.cancelAnimationFrame(firstFrameId)
      }
      if (secondFrameId) {
        window.cancelAnimationFrame(secondFrameId)
      }
    }
  }, [isSiteLoading, isSiteSelectorOpen])

  return (
    <>
      <div className='menu-item px-6'>
        <div className='menu-content px-0'>
          <div ref={siteSelectorRef} className='position-relative'>
            <button
              type='button'
              className='btn btn-sm btn-light w-100 d-flex align-items-center justify-content-between border border-gray-300'
              onClick={() => setIsSiteSelectorOpen((previous) => !previous)}
              title={selectedSiteLabel}
              aria-label={selectedSiteLabel}
            >
              <span className='text-truncate pe-2' title={selectedSiteLabel}>
                {selectedSiteLabel}
              </span>
              <i className={`bi ${isSiteSelectorOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-7`}></i>
            </button>

            {isSiteSelectorOpen ? (
              <div className='position-absolute top-100 start-0 mt-2 w-100 bg-white border border-gray-300 rounded shadow-sm p-2' style={{ zIndex: 200, maxHeight: '350px', overflowY: 'auto' }}>
                <input
                  type='search'
                  className='form-control form-control-sm mb-2'
                  placeholder='Search groups or sites'
                  value={siteSearchTerm}
                  onChange={(event) => setSiteSearchTerm(event.target.value)}
                  disabled={isSiteLoading}
                />

                {isSiteLoading ? <div className='text-muted fs-8'>Loading sites...</div> : null}

                <button
                  type='button'
                  className={`${getSiteOptionClassName(
                    selectedSiteId === null && normalizedSelectedSiteIds === null
                  )} mb-2`}
                  style={getSiteOptionStyle(selectedSiteId === null && normalizedSelectedSiteIds === null)}
                  data-site-option-selected={
                    selectedSiteId === null && normalizedSelectedSiteIds === null ? 'true' : undefined
                  }
                  onClick={() => {
                    setSelectedSiteIds(null)
                    setIsSiteSelectorOpen(false)
                  }}
                  disabled={isSiteLoading}
                >
                  Charge Hub (All Sites)
                </button>

                {!isSiteLoading ? (
                  <>
                    {filteredTopLevelSites.map((site) => {
                      const isSelected = isSiteSelected(selectedSiteId, normalizedSelectedSiteIds, site.id)
                      return (
                        <button
                          key={site.id}
                          type='button'
                            className={`${getSiteOptionClassName(isSelected)} mb-1`}
                            style={getSiteOptionStyle(isSelected)}
                            data-site-option-selected={isSelected ? 'true' : undefined}
                            onClick={() => {
                              setSelectedSiteId(site.id)
                              setIsSiteSelectorOpen(false)
                            }}
                            disabled={isSiteLoading}
                            title={site.name}
                          >
                            <i className={getSiteIconClassName(isSelected)}></i>
                            {site.name}
                          </button>
                      )
                    })}

                    {filteredRootNodes.map((node) => (
                      <SiteTreeItem
                        key={node.id}
                        node={node}
                        expandedNodeIds={expandedNodeIds}
                        toggleNode={toggleNode}
                        selectedSiteId={selectedSiteId}
                        selectedSiteIds={normalizedSelectedSiteIds}
                        setSelectedSiteId={setSelectedSiteId}
                        setSelectedSiteIds={setSelectedSiteIds}
                        siteIdsByNodeId={siteIdsByNodeId}
                        isSiteLoading={isSiteLoading}
                        forceExpanded={hasSiteSearch}
                        onSiteSelected={() => setIsSiteSelectorOpen(false)}
                      />
                    ))}

                    {filteredUngroupedSites.length > 0 ? (
                      <div className='mt-2'>
                        <div className='text-gray-500 fw-bold fs-9 text-uppercase mb-1'>Ungrouped</div>
                        {filteredUngroupedSites.map((site) => {
                          const isSelected = isSiteSelected(selectedSiteId, normalizedSelectedSiteIds, site.id)
                          return (
                            <button
                              key={site.id}
                              type='button'
                              className={`${getSiteOptionClassName(isSelected)} mb-1`}
                              style={getSiteOptionStyle(isSelected)}
                              data-site-option-selected={isSelected ? 'true' : undefined}
                              onClick={() => {
                                setSelectedSiteId(site.id)
                                setIsSiteSelectorOpen(false)
                              }}
                              disabled={isSiteLoading}
                              title={site.name}
                            >
                              <i className={getSiteIconClassName(isSelected)}></i>
                              {site.name}
                            </button>
                          )
                        })}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <AsideMenuItem
          to="/dashboard"
          title={intl.formatMessage({ id: 'MENU.DASHBOARD' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/home.svg"
          pagePermission={PAGE_PERMISSION.Dashboard}
        />
      <AsideMenuItem
          to="/chargers"
          title={intl.formatMessage({ id: 'MENU.CHARGERS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/plug.svg"
          pagePermission={PAGE_PERMISSION.Chargers}
        />
      <AsideMenuItem
          to="/faulted-assets"
          title={intl.formatMessage({ id: 'MENU.FAULTED_ASSETS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/warning.svg"
          pagePermission={PAGE_PERMISSION.Faulted_Assets}
        />
      {/* <AsideMenuItem
          to="/service-tickets"
          title={intl.formatMessage({ id: 'MENU.SERVICE_TICKETS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/headset.svg"
          pagePermission={PAGE_PERMISSION.Service_Tickets}
        /> */}
      <AsideMenuItem
          to="/foyer-view"
          title={intl.formatMessage({ id: 'MENU.FOYER_VIEW' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/monitor.svg"
          pagePermission={PAGE_PERMISSION.FoyerView}
        />
      <AsideMenuItem
          to="/reports"
          title={intl.formatMessage({ id: 'MENU.REPORTS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/chart-line.svg"
          pagePermission={PAGE_PERMISSION.Reports}
        />

      <div className="menu-item">
        <div className="menu-content">
          <div className="separator mx-1 my-4"></div>
        </div>
      </div>

      <AsideMenuItemWithSub
        to="/"
        title="Administration"
        fontIcon="bi-chat-left"
        icon="/media/icons/tims/menu-administration.svg"
        pagePermissions={[
          PAGE_PERMISSION.Admin_Users,
          PAGE_PERMISSION.Admin_Groups,
          PAGE_PERMISSION.Admin_Roles,
          PAGE_PERMISSION.Admin_Settings,
          PAGE_PERMISSION.Admin_Charts,
          PAGE_PERMISSION.Admin_Audit
        ]}>
        <AsideMenuItem
          to="/user"
          title={intl.formatMessage({ id: 'MENU.USERS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-users.svg"
          pagePermission={PAGE_PERMISSION.Admin_Users}
        />

        <AsideMenuItem
          to="/groups"
          title={intl.formatMessage({ id: 'MENU.GROUPS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-groups.svg"
          pagePermission={PAGE_PERMISSION.Admin_Groups}
        />

        <AsideMenuItem
          to="/role"
          title={intl.formatMessage({ id: 'MENU.ROLES' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-roles.svg"
          pagePermission={PAGE_PERMISSION.Admin_Roles}
        />

        {/* <AsideMenuItem
          to="/settings"
          title={intl.formatMessage({ id: 'MENU.SETTINGS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-settings.svg"
          pagePermission={PAGE_PERMISSION.Admin_Settings}
        />

        <AsideMenuItem
          to="/charts"
          title={intl.formatMessage({ id: 'MENU.CHARTS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/list-check.svg"
          pagePermission={PAGE_PERMISSION.Admin_Charts}
        />

        <AsideMenuItem
          to='/audit'
          title={intl.formatMessage({ id: 'MENU.AUDIT' })}
          fontIcon='bi-chat-left'
          icon='/media/icons/tims/menu-audit.svg'
          pagePermission={ PAGE_PERMISSION.Admin_Audit }
        /> */}
      </AsideMenuItemWithSub>
    </>
  )
}
